import { useState } from 'react'
import { router } from '@inertiajs/react'
import { User, Meeting, MeetingAttendee } from '../../types'
import MeetingRules from './MeetingRules'
import UserAvatar from '../shared/UserAvatar'
import './set-the-stage.css'

export interface SetStageVision {
  quarter: string
  has_plan: boolean
  pinnacle: string
  purpose: string
  core_business: string
  annual_due_date: string
  annual_profit: string
  annual_revenues: string
  annual_key_metric_1: string
  annual_key_metric_2: string
}

interface Props {
  members: User[]
  meeting: Meeting
  meetingAttendees?: MeetingAttendee[]
  canFacilitate?: boolean
  vision: SetStageVision | null
  sectionNumber: number
  sectionCount: number
  durationMinutes: number
}

export default function SectionSetTheStage({ members, meeting, meetingAttendees, canFacilitate, vision, sectionNumber, sectionCount, durationMinutes }: Props) {
  const [togglingIds, setTogglingIds] = useState<Set<number>>(new Set())

  const handleToggleAttendance = (attendee: MeetingAttendee) => {
    if (togglingIds.has(attendee.id)) return
    setTogglingIds(prev => new Set(prev).add(attendee.id))
    router.put(
      `/meetings/${meeting.id}/attendees/${attendee.id}`,
      { attending: !attendee.attending },
      {
        preserveScroll: true,
        preserveState: true,
        onFinish: () => {
          setTogglingIds(prev => {
            const next = new Set(prev)
            next.delete(attendee.id)
            return next
          })
        }
      }
    )
  }

  const attendees = meetingAttendees || []
  const presentCount = attendees.filter(a => a.attending).length
  const missingVisionText = vision?.has_plan
    ? `Not yet set for ${vision.quarter}`
    : `No team plan exists for ${vision?.quarter}`
  const annualFields = vision ? [
    { key: 'due-date', label: 'Due Date', value: vision.annual_due_date },
    { key: 'profit', label: 'Profit', value: vision.annual_profit },
    { key: 'revenues', label: 'Revenues', value: vision.annual_revenues },
    { key: 'metric-1', label: 'Key Metric', value: vision.annual_key_metric_1 },
    { key: 'metric-2', label: 'Key Metric', value: vision.annual_key_metric_2 },
  ].filter(field => field.value.trim()) : []

  return (
    <div className="set-stage space-y-6">
      <section className="set-stage-hero" aria-labelledby="set-stage-title">
        <img src="/images/set-stage-pinnacle-equal.png" alt="Red mountain range with a marked route to The Pinnacle" />
        <div className="set-stage-hero-content">
          <span className="set-stage-kicker">Section {sectionNumber} of {sectionCount}</span>
          <h2 id="set-stage-title">Set the Stage</h2>
          <p>{durationMinutes} minutes · orient the team for today's meeting</p>
        </div>
        <span className="set-stage-hero-caption">{meeting.team?.name || 'Team'}{vision ? ` · ${vision.quarter}` : ''}</span>
      </section>

      {vision ? (
        <section className="set-stage-vision" aria-label={`Strategic vision and execution plan for ${vision.quarter}`}>
          {([
            ['Pinnacle: The Top of Our Mountain', vision.pinnacle],
            ['Purpose: Why We Exist', vision.purpose],
            ['Core Business', vision.core_business],
          ] as const).map(([label, value]) => (
            <article className="set-stage-vision-card" key={label}>
              <h3>{label}</h3>
              <p className={value.trim() ? 'set-stage-vision-value' : 'set-stage-vision-empty'}>
                {value.trim() || missingVisionText}
              </p>
            </article>
          ))}
          <article className="set-stage-vision-card">
            <h3>Annual Growth Plan</h3>
            {annualFields.length ? (
              <dl className="set-stage-annual-list">
                {annualFields.map(field => (
                  <div className="set-stage-annual-row" key={field.key}>
                    <dt>{field.label}</dt><dd>{field.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p className="set-stage-vision-empty">{missingVisionText}</p>
            )}
          </article>
        </section>
      ) : (
        <p className="set-stage-unavailable">This team's vision is not available to you.</p>
      )}

      <MeetingRules />

      <div>
        <div className="flex items-center justify-between mb-3">
          <h4 className="text-sm font-semibold text-text-primary uppercase tracking-wide">
            Attendance
          </h4>
          {attendees.length > 0 && (
            <span className="text-sm text-text-muted">
              {presentCount} of {attendees.length} present
            </span>
          )}
        </div>

        {attendees.length > 0 ? (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* allow-unsorted-people: do NOT alphabetize. Attendance keeps the
                facilitator's custom attendee order from meeting setup. */}
            {attendees.map(attendee => (
              <button
                key={attendee.id}
                onClick={() => canFacilitate && handleToggleAttendance(attendee)}
                disabled={!canFacilitate || togglingIds.has(attendee.id)}
                className={`flex items-center gap-2 rounded-lg px-3 py-2 transition-colors text-left ${
                  attendee.attending
                    ? 'bg-white border-2 border-status-on-track/30'
                    : 'bg-white border border-stone/10'
                } ${canFacilitate ? 'cursor-pointer hover:shadow-sm' : 'cursor-default'} ${
                  togglingIds.has(attendee.id) ? 'animate-pulse' : ''
                }`}
              >
                <div className="relative">
                  <UserAvatar user={attendee.user} size="sm" dimmed={!attendee.attending} />
                  {attendee.attending && (
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-status-on-track rounded-full border-2 border-white flex items-center justify-center">
                      <svg className="w-2 h-2 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={4}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                      </svg>
                    </span>
                  )}
                </div>
                <span className={`text-sm font-medium truncate ${attendee.attending ? 'text-text-primary' : 'text-text-muted'}`}>
                  {attendee.user.full_name}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
            {/* allow-unsorted-people: do NOT alphabetize — mirrors the attendee
                order above (the fallback when no attendee records exist). */}
            {members.map(member => (
              <div key={member.id} className="flex items-center gap-2 bg-white border border-stone/10 rounded-lg px-3 py-2">
                <UserAvatar user={member} size="sm" />
                <span className="text-sm font-medium text-text-primary truncate">{member.full_name}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
